import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { useSeo } from "../hooks/useSeo";
import { ArrowRight, Search, CalendarCheck, ParkingCircle, Shield, Smartphone, MapPin, Users } from "lucide-react";
import AppShowcase from "../components/AppShowcase";
import { trackCtaClick } from "../lib/analytics";

const HOME_SLIDES = [
  { src: "/screenshots/pendler/A.png", alt: "Pendli-app: Park smartere. Kom i gang" },
  { src: "/screenshots/pendler/C.png", alt: "Pendli-app: Kart med priser nær Asker stasjon" },
  { src: "/screenshots/pendler/E.png", alt: "Pendli-app: Velg pris og dato for parkering" },
  { src: "/screenshots/pendler/H.png", alt: "Pendli-app: Betalingsoversikt og sikker betaling" },
];

export default function Home() {
  const { t, lang } = useLang();
  useSeo({ title: t.seo.home.title, description: t.seo.home.description, path: "/", lang });

  return (
    <main data-testid="home-page" className="bg-ink text-cream pt-20">
      {/* HERO */}
      <section className="relative overflow-hidden grain" data-testid="home-hero">
        {/* Bay-line motif background */}
        <div className="absolute inset-0 bay-lines opacity-60 pointer-events-none" />
        {/* Amber radial glow */}
        <div
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(232,168,0,0.18) 0%, rgba(232,168,0,0) 70%)" }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 pb-20 md:pt-24 md:pb-32 relative grid md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 border border-amber/40 px-3 py-1.5 mb-8"
          >
            <span className="w-1.5 h-1.5 bg-amber rounded-full animate-pulse" />
            <span className="text-xs font-bold tracking-widest uppercase text-amber">{t.home.heroEyebrow}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-display text-4xl sm:text-6xl md:text-6xl lg:text-7xl font-extrabold leading-[1.02] tracking-tighter"
            style={{ letterSpacing: "-0.04em" }}
            data-testid="hero-headline"
          >
            {t.home.heroTitle}<br />
            <span className="text-amber">{t.home.heroTitleAccent}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-8 md:mt-10 text-base md:text-lg text-cream/70 max-w-xl leading-relaxed"
          >
            {t.home.heroSub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-10 md:mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <Link
              to="/pendler"
              data-testid="hero-cta-pendler"
              onClick={() => trackCtaClick("pendler", "hero")}
              className="group inline-flex items-center justify-between gap-3 h-14 px-6 bg-amber text-ink font-display font-bold text-base md:text-lg hover:bg-amber-hover transition-colors"
            >
              <span>{t.home.ctaPendler}</span>
              <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/huseier"
              data-testid="hero-cta-huseier"
              onClick={() => trackCtaClick("huseier", "hero")}
              className="group inline-flex items-center justify-between gap-3 h-14 px-6 border border-amber text-cream hover:bg-amber/10 transition-colors font-display font-bold text-base md:text-lg"
            >
              <span>{t.home.ctaHuseier}</span>
              <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>

          {/* Stat row */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="mt-16 md:mt-20 grid grid-cols-3 gap-4 md:gap-8 max-w-2xl"
          >
            {[
              { num: "100%", label: "garantert parkeringsplass" },
              { num: "Nær", label: "togstasjonen" },
              { num: "0", label: "minutter med sirkling" },
            ].map((s, i) => (
              <div key={i} className="border-t border-amber/30 pt-4">
                <div className="font-display text-3xl md:text-5xl font-extrabold text-amber" style={{ letterSpacing: "-0.04em" }}>
                  {s.num}
                </div>
                <div className="text-xs md:text-sm text-cream/60 mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
          </div>

          {/* App showcase */}
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
            className="md:col-span-5 flex justify-center md:justify-end"
          >
            <AppShowcase slides={HOME_SLIDES} />
          </motion.div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="bg-cream text-ink py-20 md:py-32" data-testid="home-problem">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <span className="text-xs font-bold tracking-widest uppercase text-orange">
              {t.home.problemEyebrow}
            </span>
            <div className="w-14 h-1 bg-ink mt-4" />
          </div>
          <div className="md:col-span-8">
            <h2 className="font-display text-3xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tighter" style={{ letterSpacing: "-0.04em" }}>
              {t.home.problemTitle}
            </h2>
            <p className="mt-7 text-lg md:text-2xl text-ink/70 leading-relaxed max-w-2xl">
              {t.home.problemBody}
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-ink py-20 md:py-32 relative overflow-hidden" data-testid="home-how">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14 md:mb-20 max-w-3xl">
            <span className="text-xs font-bold tracking-widest uppercase text-amber">
              {t.home.howEyebrow}
            </span>
            <h2 className="mt-4 font-display text-3xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tighter" style={{ letterSpacing: "-0.04em" }}>
              {t.home.howTitle}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {[
              { Icon: Search, title: t.home.step1Title, body: t.home.step1Body, n: "01" },
              { Icon: CalendarCheck, title: t.home.step2Title, body: t.home.step2Body, n: "02" },
              { Icon: ParkingCircle, title: t.home.step3Title, body: t.home.step3Body, n: "03" },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="relative bg-amber-deep p-8 md:p-10 l-bracket-tl l-bracket-tr l-bracket-bl l-bracket-br border border-amber/20"
                data-testid={`how-step-${i+1}`}
              >
                <div className="flex items-start justify-between mb-8">
                  <s.Icon className="text-amber" size={32} strokeWidth={1.5} />
                  <span className="font-display text-5xl font-extrabold text-amber/30" style={{ letterSpacing: "-0.04em" }}>
                    {s.n}
                  </span>
                </div>
                <h3 className="font-display text-2xl font-bold tracking-tight mb-3">{s.title}</h3>
                <p className="text-cream/70 leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIENCE SPLIT */}
      <section className="bg-ink py-20 md:py-32 border-t border-amber/10" data-testid="home-audience">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <h2 className="font-display text-3xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tighter mb-14 md:mb-20 max-w-3xl" style={{ letterSpacing: "-0.04em" }}>
            {t.home.audienceTitle}
          </h2>
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <Link
              to="/pendler"
              data-testid="audience-card-pendler"
              onClick={() => trackCtaClick("pendler", "audience_section")}
              className="group relative bg-cream text-ink p-8 md:p-12 hover:bg-amber transition-colors overflow-hidden"
            >
              <span className="text-xs font-bold tracking-widest uppercase text-orange group-hover:text-ink/70">
                {t.common.forCommuters}
              </span>
              <h3 className="mt-3 font-display text-3xl md:text-5xl font-extrabold leading-[1.05] tracking-tighter" style={{ letterSpacing: "-0.04em" }}>
                {t.home.audiencePendlerTitle}
              </h3>
              <p className="mt-5 text-base md:text-lg text-ink/70 group-hover:text-ink/80 max-w-md leading-relaxed">
                {t.home.audiencePendlerBody}
              </p>
              <div className="mt-10 inline-flex items-center gap-2 font-display font-bold border-b-2 border-ink pb-1">
                {t.common.learnMore}
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </div>
            </Link>

            <Link
              to="/huseier"
              data-testid="audience-card-huseier"
              onClick={() => trackCtaClick("huseier", "audience_section")}
              className="group relative bg-amber text-ink p-8 md:p-12 hover:bg-amber-hover transition-colors overflow-hidden"
            >
              <span className="text-xs font-bold tracking-widest uppercase text-ink/80">
                {t.common.forHomeowners}
              </span>
              <h3 className="mt-3 font-display text-3xl md:text-5xl font-extrabold leading-[1.05] tracking-tighter" style={{ letterSpacing: "-0.04em" }}>
                {t.home.audienceHuseierTitle}
              </h3>
              <p className="mt-5 text-base md:text-lg text-ink/80 max-w-md leading-relaxed">
                {t.home.audienceHuseierBody}
              </p>
              <div className="mt-10 inline-flex items-center gap-2 font-display font-bold border-b-2 border-ink pb-1">
                {t.common.learnMore}
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="bg-cream text-ink py-20 md:py-32" data-testid="home-trust">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <h2 className="font-display text-3xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tighter mb-14 md:mb-20 max-w-3xl" style={{ letterSpacing: "-0.04em" }}>
            {t.home.trustTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            {[
              { Icon: Smartphone, t: t.home.trust1, b: t.home.trust1Body },
              { Icon: Users, t: t.home.trust2, b: t.home.trust2Body },
              { Icon: Shield, t: t.home.trust3, b: t.home.trust3Body },
              { Icon: MapPin, t: t.home.trust4, b: t.home.trust4Body },
            ].map((it, i) => (
              <div key={i} className="flex gap-5 border-t border-ink/15 pt-7" data-testid={`trust-item-${i+1}`}>
                <div className="w-12 h-12 bg-ink text-amber flex items-center justify-center shrink-0">
                  <it.Icon size={22} strokeWidth={1.6} />
                </div>
                <div>
                  <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight">{it.t}</h3>
                  <p className="mt-2 text-ink/70 leading-relaxed">{it.b}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ASKER BANNER */}
      <section className="relative bg-ink overflow-hidden" data-testid="asker-banner">
        <div className="absolute inset-0 bay-lines opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 relative">
          <div className="border border-amber p-8 md:p-14 relative l-bracket-tl l-bracket-tr l-bracket-bl l-bracket-br bg-amber-deep/40 grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <span className="text-xs font-bold tracking-widest uppercase text-amber">Pilot · Norge</span>
              <h2 className="mt-4 font-display text-3xl md:text-5xl font-extrabold leading-[1.05] tracking-tighter" style={{ letterSpacing: "-0.04em" }}>
                {t.home.bannerTitle}
              </h2>
              <p className="mt-5 text-cream/70 text-base md:text-lg leading-relaxed max-w-xl">
                {t.home.bannerBody}
              </p>
            </div>
            <div className="md:col-span-5 flex md:justify-end gap-3 flex-wrap">
              <Link
                to="/pendler"
                data-testid="banner-cta-pendler"
                onClick={() => trackCtaClick("pendler", "banner")}
                className="inline-flex items-center gap-2 h-12 px-5 bg-amber text-ink font-display font-bold hover:bg-amber-hover transition-colors"
              >
                {t.home.ctaPendler} <ArrowRight size={18} />
              </Link>
              <Link
                to="/huseier"
                data-testid="banner-cta-huseier"
                onClick={() => trackCtaClick("huseier", "banner")}
                className="inline-flex items-center gap-2 h-12 px-5 border border-amber text-cream hover:bg-amber/10 transition-colors font-display font-bold"
              >
                {t.home.ctaHuseier} <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-ink py-20 md:py-32 border-t border-amber/10" data-testid="home-faq">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <h2 className="font-display text-3xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tighter mb-14 md:mb-16" style={{ letterSpacing: "-0.04em" }}>
            {t.home.faqTitle}
          </h2>
          <div className="space-y-4">
            {t.home.faqs.map((f, i) => (
              <details
                key={i}
                data-testid={`home-faq-${i+1}`}
                className="group border-t border-amber/20 py-5 cursor-pointer"
              >
                <summary className="flex items-center justify-between gap-4 list-none">
                  <h3 className="font-display text-lg md:text-2xl font-bold tracking-tight group-hover:text-amber transition-colors">
                    {f.q}
                  </h3>
                  <span className="text-amber text-2xl font-bold leading-none transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-4 text-cream/70 text-base md:text-lg leading-relaxed pr-8">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
